/**
 * Marker service for API communication with marker endpoints.
 */

import apiClient from '../api/client';

export interface MarkerResponse {
    id: string;
    event_id: string;
    owner_id: string;
    faction_id: string | null;
    name: string;
    sidc: string;
    text_modifiers: Record<string, unknown>;
    is_deleted: boolean;
    latitude: number | null;
    longitude: number | null;
    position_time: string | null;
}

export interface MarkerListResponse {
    markers: MarkerResponse[];
}

export interface MarkerCreate {
    name: string;
    sidc: string;
    faction_id?: string | null;
    text_modifiers?: Record<string, unknown>;
    latitude?: number | null;
    longitude?: number | null;
}

export interface MarkerUpdate {
    name?: string;
    sidc?: string;
    faction_id?: string | null;
    text_modifiers?: Record<string, unknown>;
}

/**
 * Get all markers for an event, optionally filtered by faction.
 */
export async function getMarkers(
    eventId: string,
    factionId?: string
): Promise<MarkerListResponse> {
    const params = factionId ? { faction_id: factionId } : {};
    const response = await apiClient.get<MarkerListResponse>(
        `/api/v1/events/${eventId}/markers`,
        { params }
    );
    return response.data;
}

/**
 * Create a new marker.
 */
export async function createMarker(
    eventId: string,
    data: MarkerCreate
): Promise<MarkerResponse> {
    const response = await apiClient.post<MarkerResponse>(
        `/api/v1/events/${eventId}/markers`,
        data
    );
    return response.data;
}

/**
 * Update an existing marker.
 */
export async function updateMarker(
    eventId: string,
    markerId: string,
    data: MarkerUpdate
): Promise<MarkerResponse> {
    const response = await apiClient.patch<MarkerResponse>(
        `/api/v1/events/${eventId}/markers/${markerId}`,
        data
    );
    return response.data;
}

/**
 * Record a new position for a marker.
 */
export async function recordMarkerPosition(
    eventId: string,
    markerId: string,
    lat: number,
    lng: number
): Promise<MarkerResponse> {
    const response = await apiClient.put<MarkerResponse>(
        `/api/v1/events/${eventId}/markers/${markerId}/position`,
        { latitude: lat, longitude: lng }
    );
    return response.data;
}

/**
 * Delete a marker.
 */
export async function deleteMarker(
    eventId: string,
    markerId: string
): Promise<void> {
    await apiClient.delete(`/api/v1/events/${eventId}/markers/${markerId}`);
}

